import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ShiftManagement = () => {
  const [activeTab, setActiveTab] = useState('shiftSetup');
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState({
    departments: true,
    employees: false,
    shifts: false
  });
  const [error, setError] = useState(null);

  // Utility function to convert 24-hour time to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const minute = minutes;
    
    if (hour === 0) {
      return `12:${minute} AM`;
    } else if (hour < 12) {
      return `${hour}:${minute} AM`;
    } else if (hour === 12) {
      return `12:${minute} PM`;
    } else {
      return `${hour - 12}:${minute} PM`;
    }
  };
  
  // State for shift setup
  const [shiftSetup, setShiftSetup] = useState({
    name: '',
    startTime: '',
    endTime: ''
  });
  
  const [shiftAssignment, setShiftAssignment] = useState({
    company: localStorage.getItem('selectedCompanyName') || 'My Company',
    companyId: localStorage.getItem('selectedCompanyId') || '',
    department: '',
    employee: '',
    shift: '',
    date: ''
  });
  
  const [editingShift, setEditingShift] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [shifts, setShifts] = useState([]);
  
  const [assignments, setAssignments] = useState([
    { id: 1, company: 'ABC Corp', department: 'Sales', employee: 'John Doe', shift: 'Morning Shift', date: '2023-05-15' },
    { id: 2, company: 'XYZ Ltd', department: 'Marketing', employee: 'Jane Smith', shift: 'Evening Shift', date: '2023-05-15' }
  ]);
  
  const overtimeData = [
    { employee: 'John Doe', date: '2023-05-15', hoursWorked: 9.5, overtime: 1.5 },
    { employee: 'Jane Smith', date: '2023-05-15', hoursWorked: 10, overtime: 2 }
  ];

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      const companyId = localStorage.getItem('selectedCompanyId');
      const token = localStorage.getItem('authToken');
      
      if (!companyId || !token) {
        setError('No company selected or user not authenticated');
        setLoading(prev => ({ ...prev, departments: false }));
        return;
      }

      try {
        const response = await axios.get(
          `https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDepartments(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching departments:', err);
        setError('Failed to fetch departments');
        setDepartments([]);
      } finally {
        setLoading(prev => ({ ...prev, departments: false }));
      }
    };

    fetchDepartments();
  }, [shiftAssignment.companyId]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem('authToken');
      const companyId = localStorage.getItem('selectedCompanyId');
      
      if (!token) {
        setError('No access token found. Please log in.');
        setLoading(prev => ({ ...prev, employees: false }));
        return;
      }

      if (!companyId) {
        setError('No company selected. Please select a company first.');
        setLoading(prev => ({ ...prev, employees: false }));
        return;
      }

      try {
        setLoading(prev => ({ ...prev, employees: true }));
        const response = await axios.get(
          `https://customersupport-jhy9.onrender.com/api/employees/company/${companyId}`,
          { 
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        // Store all employees and filter them in the UI based on selected department
        setEmployees(response.data);
      } catch (err) {
        console.error('Error fetching employees:', err.response?.data || err.message);
        setError(`Failed to fetch employees: ${err.response?.data?.message || err.message || 'Unable to connect to server'}`);
        setEmployees([]);
      } finally {
        setLoading(prev => ({ ...prev, employees: false }));
      }
    };

    fetchEmployees();
  }, []);

  // Fetch shifts
  useEffect(() => {
    const fetchShifts = async () => {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('No access token found. Please log in.');
        setLoading(prev => ({ ...prev, shifts: false }));
        return;
      }

      try {
        setLoading(prev => ({ ...prev, shifts: true }));
        const response = await axios.get(
          'https://customersupport-jhy9.onrender.com/api/shifts/',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShifts(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching shifts:', err);
        setError('Failed to fetch shifts');
        setShifts([]);
      } finally {
        setLoading(prev => ({ ...prev, shifts: false }));
      }
    };

    fetchShifts();
  }, []);

  // Handle shift setup form submission
  const handleShiftSetup = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      setError('No access token found. Please log in.');
      return;
    }

    try {
      if (editingShift) {
        // Update existing shift
        const response = await axios.put(
          `https://customersupport-jhy9.onrender.com/api/shifts/${editingShift._id}`,
          shiftSetup,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShifts(shifts.map(shift => 
          shift._id === editingShift._id ? response.data : shift
        ));
        alert(`Shift "${shiftSetup.name}" updated successfully!`);
        setEditingShift(null);
      } else {
        // Create new shift
        const response = await axios.post(
          'https://customersupport-jhy9.onrender.com/api/shifts/',
          shiftSetup,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShifts([...shifts, response.data]);
        alert(`Shift "${shiftSetup.name}" created successfully!`);
      }
      setShiftSetup({ name: '', startTime: '', endTime: '' });
    } catch (err) {
      console.error('Error saving shift:', err);
      setError(`Failed to ${editingShift ? 'update' : 'create'} shift: ${err.response?.data?.message || err.message}`);
    }
  };

  // Handle shift assignment form submission
  const handleShiftAssignment = (e) => {
    e.preventDefault();
    const companyName = localStorage.getItem('selectedCompanyName') || 'My Company';
    const companyId = localStorage.getItem('selectedCompanyId') || '';
    
    const assignmentData = {
      ...shiftAssignment,
      company: companyName,
      companyId: companyId
    };

    if (editingAssignment) {
      setAssignments(assignments.map(assignment => 
        assignment.id === editingAssignment.id ? {...assignmentData, id: editingAssignment.id} : assignment
      ));
      alert(`Shift assignment for ${assignmentData.employee} updated successfully!`);
      setEditingAssignment(null);
    } else {
      const newAssignment = {
        id: assignments.length > 0 ? Math.max(...assignments.map(a => a.id)) + 1 : 1,
        ...assignmentData
      };
      setAssignments([...assignments, newAssignment]);
      alert(`Shift assigned to ${assignmentData.employee} successfully!`);
    }
    
    setShiftAssignment({
      company: companyName,
      companyId: companyId,
      department: '',
      employee: '',
      shift: '',
      date: ''
    });
  };

  // Cancel edit
  const cancelEdit = () => {
    setShiftSetup({ name: '', startTime: '', endTime: '' });
    setShiftAssignment({ 
      company: localStorage.getItem('selectedCompanyName') || 'My Company', 
      companyId: localStorage.getItem('selectedCompanyId') || '', 
      department: '', 
      employee: '', 
      shift: '', 
      date: '' 
    });
    setEditingShift(null);
    setEditingAssignment(null);
  };

  // Edit and Delete shift functions
  const editShift = (shift) => {
    setShiftSetup({
      name: shift.name,
      startTime: shift.startTime,
      endTime: shift.endTime
    });
    setEditingShift(shift);
    setActiveTab('shiftSetup');
  };

  const deleteShift = async (shiftId) => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('No access token found. Please log in.');
        return;
      }

      try {
        await axios.delete(
          `https://customersupport-jhy9.onrender.com/api/shifts/${shiftId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShifts(shifts.filter(shift => shift._id !== shiftId));
        alert('Shift deleted successfully!');
      } catch (err) {
        console.error('Error deleting shift:', err);
        setError(`Failed to delete shift: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const editAssignment = (assignment) => {
    setShiftAssignment({
      company: assignment.company,
      companyId: assignment.companyId,
      department: assignment.department,
      employee: assignment.employee,
      shift: assignment.shift,
      date: assignment.date
    });
    setEditingAssignment(assignment);
    setActiveTab('shiftAssignment');
  };

  const deleteAssignment = (assignmentId) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      setAssignments(assignments.filter(assignment => assignment.id !== assignmentId));
      alert('Assignment deleted successfully!');
    }
  };

  // Handle input changes for shift assignment form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If department changes, reset the selected employee
    if (name === 'department') {
      setShiftAssignment(prev => ({
        ...prev,
        [name]: value,
        employee: '' // Reset employee when department changes
      }));
    } else {
      setShiftAssignment(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="flex flex-wrap">
            <button
              className={`px-6 py-4 font-medium flex items-center ${activeTab === 'shiftSetup' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              onClick={() => setActiveTab('shiftSetup')}
            >
              Shift Setup
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {shifts.length}
              </span>
            </button>
            {/* <button
              className={`px-6 py-4 font-medium flex items-center ${activeTab === 'shiftAssignment' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              onClick={() => setActiveTab('shiftAssignment')}
             >
              Shift Assignment
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {assignments.length}
              </span>
            </button> */}
            {/* <button
              className={`px-6 py-4 font-medium flex items-center ${activeTab === 'overtimeTracking' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              onClick={() => setActiveTab('overtimeTracking')}
             >
              Overtime Tracking
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {overtimeData.length}
              </span>
            </button> */}
          </div>
        </div>
        
        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          {/* Shift Setup Tab */}
          {activeTab === 'shiftSetup' && (
            <div>
              <div className="flex items-center mb-6">
                <div className="p-2 rounded-md bg-blue-100 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Shift Setup</h2>
              </div>
              
              <form onSubmit={handleShiftSetup} className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-gray-700 mb-2">Shift Name</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={shiftSetup.name}
                      onChange={(e) => setShiftSetup({...shiftSetup, name: e.target.value})}
                      placeholder="e.g., Morning Shift"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 mb-2">Start Time</label>
                      <input 
                        type="time" 
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={shiftSetup.startTime}
                        onChange={(e) => setShiftSetup({...shiftSetup, startTime: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 mb-2">End Time</label>
                      <input 
                        type="time" 
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={shiftSetup.endTime}
                        onChange={(e) => setShiftSetup({...shiftSetup, endTime: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button 
                    type="submit" 
                    className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200 flex items-center"
                    disabled={loading.shifts}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={editingShift ? "M5 13l4 4L19 7" : "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"} />
                    </svg>
                    {editingShift ? 'Update Shift' : 'Create Shift'}
                  </button>
                  
                  {editingShift && (
                    <button 
                      type="button" 
                      onClick={cancelEdit}
                      className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition duration-200"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
              
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Existing Shifts</h3>
                {loading.shifts ? (
                  <p className="text-gray-500 text-center py-4">Loading shifts...</p>
                ) : shifts.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No shifts created yet.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Shift Name</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Start Time</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">End Time</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {shifts.map((shift) => (
                          <tr key={shift._id} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4">{shift.name}</td>
                            <td className="py-3 px-4 font-medium text-blue-600">{formatTimeTo12Hour(shift.startTime)}</td>
                            <td className="py-3 px-4 font-medium text-green-600">{formatTimeTo12Hour(shift.endTime)}</td>
                            <td className="py-3 px-4">
                              <div className="flex space-x-2">
                                <button 
                                  onClick={() => editShift(shift)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Edit
                                </button>
                                <button 
                                  onClick={() => deleteShift(shift._id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Shift Assignment Tab */}
          {/* {activeTab === 'shiftAssignment' && (
            <div>
              <div className="flex items-center mb-6">
                <div className="p-2 rounded-md bg-green-100 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Shift Assignment</h2>
              </div>
              
              <form onSubmit={handleShiftAssignment} className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-gray-700 mb-2">Company</label>
                    <div className="px-4 py-2 bg-gray-100 rounded-md border border-gray-300">
                      {shiftAssignment.company}
                    </div>
                    <input type="hidden" name="company" value={shiftAssignment.company} />
                    <input type="hidden" name="companyId" value={shiftAssignment.companyId} />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 mb-2">Department</label>
                    <select 
                      name="department"
                      value={shiftAssignment.department}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Department</option>
                      {departments.map((dept) => (
                        <option key={dept._id} value={dept._id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 mb-2">Select Employee</label>
                    <select 
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      name="employee"
                      value={shiftAssignment.employee}
                      onChange={handleInputChange}
                      required
                      disabled={loading.employees || !shiftAssignment.department}
                    >
                      <option value="">
                        {!shiftAssignment.department 
                          ? 'Select a department first' 
                          : 'Select an employee'}
                      </option>
                      {employees
                        .filter(emp => emp.department === shiftAssignment.department)
                        .map((emp) => (
                          <option key={emp._id} value={emp._id}>
                            {emp.teamMemberName}
                          </option>
                      ))}
                    </select>
                    {loading.employees && (
                      <p className="text-sm text-gray-500 mt-1">Loading employees...</p>
                    )}
                    {!loading.employees && employees.length === 0 && (
                      <p className="text-sm text-red-500 mt-1">No employees found</p>
                    )}
                    {!loading.employees && shiftAssignment.department && employees.filter(emp => emp.department === shiftAssignment.department).length === 0 && (
                      <p className="text-sm text-yellow-600 mt-1">No employees found in the selected department</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 mb-2">Select Shift</label>
                    <select 
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={shiftAssignment.shift}
                      onChange={(e) => setShiftAssignment({...shiftAssignment, shift: e.target.value})}
                      required
                    >
                      <option value="">Select a shift</option>
                      {shifts.map((shift) => (
                        <option key={shift._id} value={shift.name}>{shift.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 mb-2">Date</label>
                    <input 
                      type="date" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={shiftAssignment.date}
                      onChange={(e) => setShiftAssignment({...shiftAssignment, date: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button 
                    type="submit" 
                    className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200 flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={editingAssignment ? "M5 13l4 4L19 7" : "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"} />
                    </svg>
                    {editingAssignment ? 'Update Assignment' : 'Assign Shift'}
                  </button>
                  
                  {editingAssignment && (
                    <button 
                      type="button" 
                      onClick={cancelEdit}
                      className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition duration-200"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
              
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Existing Assignments</h3>
                {assignments.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No assignments created yet.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Department</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Shift</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {assignments.map((assignment) => (
                          <tr key={assignment.id} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4">{assignment.company}</td>
                            <td className="py-3 px-4">{assignment.employee}</td>
                            <td className="py-3 px-4">{assignment.department}</td>
                            <td className="py-3 px-4">{assignment.shift}</td>
                            <td className="py-3 px-4">{assignment.date}</td>
                            <td className="py-3 px-4">
                              <div className="flex space-x-2">
                                <button 
                                  onClick={() => editAssignment(assignment)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Edit
                                </button>
                                <button 
                                  onClick={() => deleteAssignment(assignment.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )} */}
          
          {/* Overtime Tracking Tab */}
          {/* {activeTab === 'overtimeTracking' && (
            <div>
              <div className="flex items-center mb-6">
                <div className="p-2 rounded-md bg-red-100 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Overtime Tracking</h2>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600">Calculate OT if employee works beyond shift hours</p>
              </div>
              
              <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Hours Worked</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Overtime</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {overtimeData.map((data, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4">{data.employee}</td>
                        <td className="py-3 px-4">{data.date}</td>
                        <td className="py-3 px-4">{data.hoursWorked} hrs</td>
                        <td className="py-3 px-4 text-red-600 font-semibold">{data.overtime} hrs</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <button className="bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition duration-200 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Generate Overtime Report
              </button>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default ShiftManagement;